// /////////////////////////////////////////////////////////////
// [ファイル] SfaStructureChips.tsx
// [概要] 推進案件機能統合（リリース1）で使う小さな表示部品
//        タイプ（仕入あり／売上のみ）、仕入状況（契約前／契約済／決済済）、
//        フェーズ（仕入・売上共通。0.未着手 は「販売前」、仕入側の 6・7 は「決済」「失注（仕入断念）」と表示）、予定超過・集計対象外の注記
// /////////////////////////////////////////////////////////////
import React from 'react';
import { Chip, SxProps, Theme } from '@mui/material';
import { PHASE_COLORS, Phase } from '../../constants/masterData';
import { BuyStatus, ProjectKind, IDLE_PHASE, IDLE_LABEL, purchasePhaseLabel } from '../../hooks/useSfaStructure';

const base: SxProps<Theme> = { fontWeight: 600, fontSize: '0.72rem', borderRadius: '6px', height: 20, '& .MuiChip-label': { px: 0.9 } };

export function SfaTypeChip({ kind, sx }: { kind: ProjectKind | null | undefined; sx?: SxProps<Theme> }) {
  if (!kind) return null;
  const buy = kind === 'purchase';
  return (
    <Chip
      label={buy ? '仕入あり' : '売上のみ'}
      size="small"
      sx={{ ...base, bgcolor: buy ? '#e8f5e9' : '#e3f2fd', color: buy ? '#2e7d32' : '#1565c0', ...sx } as SxProps<Theme>}
    />
  );
}

const BUY_STATUS: Record<BuyStatus, { label: string; bg: string; fg: string }> = {
  before: { label: '契約前', bg: '#eceff1', fg: '#455a64' },
  contracted: { label: '契約済', bg: '#fff3e0', fg: '#e65100' },
  settled: { label: '決済済', bg: '#e8f5e9', fg: '#2e7d32' },
};

export function SfaBuyStatusChip({ status, sx }: { status: BuyStatus | null | undefined; sx?: SxProps<Theme> }) {
  if (!status) return null;
  const s = BUY_STATUS[status];
  return <Chip label={s.label} size="small" sx={{ ...base, bgcolor: s.bg, color: s.fg, ...sx } as SxProps<Theme>} />;
}

// purchase を付けると仕入フェーズの表示名（値は同じ。色も同じ）
export function UnitPhaseChip({ phase, purchase, sx }: { phase: string | null | undefined; purchase?: boolean; sx?: SxProps<Theme> }) {
  if (!phase) return null;
  if (phase === IDLE_PHASE) {
    return <Chip label={IDLE_LABEL} size="small" variant="outlined" sx={{ ...base, color: '#9e9e9e', borderStyle: 'dashed', borderColor: '#bdbdbd', fontWeight: 500, ...sx } as SxProps<Theme>} />;
  }
  const color = PHASE_COLORS[phase as Phase];
  return <Chip label={purchase ? purchasePhaseLabel(phase) : phase} size="small" sx={{ ...base, bgcolor: color?.bg ?? '#eeeeee', color: color?.fg ?? '#444444', ...sx } as SxProps<Theme>} />;
}

export function OverdueChip({ show }: { show: boolean | null | undefined }) {
  if (!show) return null;
  return <Chip label="予定超過" size="small" sx={{ ...base, bgcolor: '#ffebee', color: '#c62828' } as SxProps<Theme>} />;
}

export function BookingChip({ ym, confirmed }: { ym: string | null | undefined; confirmed: boolean }) {
  if (!ym) return null;
  const [y, m] = ym.split('-');
  return (
    <Chip
      label={`${y}年${Number(m)}月 ${confirmed ? '確定' : '予定'}`}
      size="small"
      sx={{ ...base, bgcolor: confirmed ? '#e8f5e9' : '#eceff1', color: confirmed ? '#2e7d32' : '#455a64' } as SxProps<Theme>}
    />
  );
}
